import { useMemo } from 'react';

import { TUseAsyncOptionsParams } from '@/shared/components/ui/form/components/searchable-select/hooks/use-async-options/use-async-options.types';
import { useOptions } from '@/shared/components/ui/form/components/searchable-select/hooks/use-options/use-options';
import { NameFilterBuilder } from '@/shared/filters/entity-filters/name-filter-builder/name-filter-builder';
import { useDirectFilter } from '@/shared/filters/hooks/use-direct-filter/use-direct-filter';

export const useAsyncOptions = <TData, TDataShape>({
  debouncedSearch,
  rtkQueryArgs,
  useRTKQuery,
  transformData,
  value,
  getItemId,
  disableNameLower = false,
  searchField,
  disableSort,
  pageSize,
  getPageCount,
}: TUseAsyncOptionsParams<TData, TDataShape>) => {
  const nameFilter = useDirectFilter(
    new NameFilterBuilder({
      disableFieldMapping: disableNameLower,
      field: searchField,
    }),
    {
      name: debouncedSearch,
    },
  );
  const filter = { ...nameFilter, ...rtkQueryArgs?.filter };
  const { data, isLoading, isFetching, isError } = useRTKQuery({
    ...rtkQueryArgs,
    filter: debouncedSearch ? filter : rtkQueryArgs?.filter,
    ...(pageSize ? { pagination: { page: 1, pageSize } } : {}),
  });

  const loading = isLoading || isFetching;
  const hasMoreOptions = (getPageCount?.(data) ?? 0) > 1;
  // `data` is RTK Query's memoized selector result — it keeps the same
  // reference across re-renders when the underlying cache entry is
  // unchanged (e.g. a polling parent re-rendering with identical results).
  // Recomputing here only when `data` or `transformData` actually change
  // preserves that reference stability through to the rendered options.
  const options = useMemo(() => transformData(data), [data, transformData]);

  const { isSelected, groupedOptions } = useOptions({
    options,
    getItemId,
    value,
    disableSort,
  });

  return {
    options: groupedOptions,
    loading,
    isSelected,
    loadError: Boolean(isError),
    hasMoreOptions,
  };
};
